import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { REGULARISATION_PATHS } from './lib/compta/regularisationsNav'
import { lazy, Suspense, useEffect } from 'react'
import { supabase } from './lib/supabase'
import { useDossierStore } from './store/dossierStore'
import { Scale, PenLine, BarChart2, Download, Package } from 'lucide-react'
import MobileAwareLanding from './pages/MobileAwareLanding'
import Onboarding from './pages/Onboarding'
import AuthPage from './pages/AuthPage'
import AuthCabinet from './pages/AuthCabinet'
import AuthInvite from './pages/AuthInvite'
import AuthVerificationEmail from './pages/AuthVerificationEmail'
import Layout from './components/layout/Layout'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { MobileProtectedRoute } from './components/auth/MobileProtectedRoute'
import MobileLayout from './components/mobile/MobileLayout'
import { ErrorBoundary } from './components/ErrorBoundary'
import SaisieOperations from './pages/Gestion/Transactions/SaisieOperations'
import TvaPage from './pages/Transactions/TvaPage'
import CompteDeResultat from './pages/CompteDeResultat'
import ExportOperations from './pages/Export/ExportOperations'
import ComptaPlaceholder from './pages/Compta/ComptaPlaceholder'
import FiscaliteHome from './pages/Compta/Fiscalite/FiscaliteHome'
import FacturesVenteCompta from './pages/Compta/FacturesVenteCompta'
import FacturesAchatCompta from './pages/Compta/FacturesAchatCompta'
import TransactionsCompta from './pages/Compta/TransactionsCompta'
import RapprochementRevision from './pages/Compta/Revision/Rapprochement'
import FacturePublic from './pages/Facturation/FacturePublic'
import ProduitsImport from './pages/Facturation/ProduitsImport'
import MonCompte from './pages/MonCompte'
import LoginCollaborateur from './pages/equipe/LoginCollaborateur'
import InvitationCollaborateur from './pages/equipe/InvitationCollaborateur'
import CockpitLayout from './pages/Cockpit'
import CockpitDashboard from './pages/Cockpit/Dashboard'
import CockpitUsers from './pages/Cockpit/Users'
import CockpitUserDetail from './pages/Cockpit/UserDetail'
import CockpitDossiers from './pages/Cockpit/Dossiers'
import CockpitDossierDetail from './pages/Cockpit/DossierDetail'
import CockpitAbonnements from './pages/Cockpit/Abonnements'
import CockpitActivite from './pages/Cockpit/Activite'

// Pages lourdes -> lazy loading
const Transactions = lazy(() => import('./pages/Transactions/Transactions'))
const TransactionsRapprochement = lazy(() => import('./pages/Transactions/TransactionsRapprochement'))
const FacturesAchat = lazy(() => import('./pages/Facturation/FacturesAchat'))
const FacturesVente = lazy(() => import('./pages/Facturation/FacturesVente'))
const ImmobilisationsPage = lazy(() => import('./pages/Compta/Revision/Immobilisations'))
const BalanceGenerale = lazy(() => import('./pages/Compta/Revision/BalanceGenerale'))
const GrandLivre = lazy(() => import('./pages/Compta/Revision/GrandLivre'))
const EtatsFinanciersPage = lazy(() => import('./pages/Compta/EtatsFinanciers/index'))
const DeclarationTVA = lazy(() => import('./pages/Compta/Fiscalite/DeclarationTVA'))
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'))
const Parametres = lazy(() => import('./pages/Parametres/Parametres'))
const ParametresEntreprise = lazy(() => import('./pages/Parametres/ParametresEntreprise'))
const ParametresExercice = lazy(() => import('./pages/Parametres/ParametresExercice'))
const PlanComptable = lazy(() => import('./pages/Parametres/PlanComptable'))
const CabinetDashboard = lazy(() => import('./pages/Cabinet/CabinetDashboard'))
const OrganisationCabinet = lazy(() => import('./pages/Cabinet/OrganisationCabinet'))
const Clients = lazy(() => import('./pages/Facturation/Clients'))
const Fournisseurs = lazy(() => import('./pages/Facturation/Fournisseurs'))
const Produits = lazy(() => import('./pages/Facturation/Produits'))
const Devis = lazy(() => import('./pages/Facturation/Devis'))
const DevisCreate = lazy(() => import('./pages/Facturation/DevisCreate'))
const DevisDetail = lazy(() => import('./pages/Facturation/DevisDetail'))
const FactureVenteCreate = lazy(() => import('./pages/Facturation/FactureVenteCreate'))
const FactureVenteDetail = lazy(() => import('./pages/Facturation/FactureVenteDetail'))
const ImportFacturesVente = lazy(() => import('./pages/Facturation/ImportFacturesVente'))
const FacturesVenteImportees = lazy(() => import('./pages/Gestion/factures-vente/FacturesVenteImportees'))
const ValiderImportVente = lazy(() => import('./pages/Facturation/ValiderImportVente'))
const JournauxComptables = lazy(() => import('./pages/Compta/JournauxComptables'))
const ImportBalance = lazy(() => import('./pages/Compta/ImportBalance'))
const ImportFEC = lazy(() => import('./pages/Compta/ImportFEC'))
const SaisieMasse = lazy(() => import('./pages/Compta/SaisieMasse'))
const ClotureExercicePage = lazy(() => import('./pages/Compta/ClotureExercice'))
const LettragePage = lazy(() => import('./pages/Compta/Lettrage/index'))
const DeclarationsTVABoard = lazy(() => import('./pages/Compta/Fiscalite/DeclarationsTVABoard'))
const RegularisationCCA = lazy(() => import('./pages/Compta/regularisations/ChargesConstateesAvance'))
const RegularisationPCA = lazy(() => import('./pages/Compta/regularisations/ProduitsConstatesAvance'))
const RegularisationCAP = lazy(() => import('./pages/Compta/regularisations/ChargesAPayer'))
const RegularisationPAR = lazy(() => import('./pages/Compta/regularisations/ProduitsARecevoir'))
const RegularisationFNP = lazy(() => import('./pages/Compta/regularisations/FacturesNonParvenues'))
const RegularisationFAE = lazy(() => import('./pages/Compta/regularisations/FacturesAEtablir'))
const FeuillesRevisionPage = lazy(() => import('./pages/Compta/Revision/FeuillesRevision'))
const DossierRevisionPage = lazy(() => import('./pages/revision/DossierRevision'))
const MobileImport = lazy(() => import('./pages/mobile/MobileImport'))
const MobileLogin = lazy(() => import('./pages/mobile/MobileLogin'))
const MobileDashboard = lazy(() => import('./pages/mobile/MobileDashboard'))
const MobileFactures = lazy(() => import('./pages/mobile/MobileFactures'))
const MobileDossierSelect = lazy(() => import('./pages/mobile/MobileDossierSelect'))
const MobileProfil = lazy(() => import('./pages/mobile/MobileProfil'))
const DossierClientGED = lazy(() => import('./pages/DossierClient/GED'))

function PageLoader() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-orange-200 border-t-orange-500" />
        <p className="text-sm text-slate-500">Chargement...</p>
      </div>
    </div>
  )
}

/** Diagnostics auth / routing (logs console). À retirer une fois le flux membre stabilisé. */
function AuthRoutingDiagnostics() {
  const location = useLocation()
  const activeDossier = useDossierStore((s) => s.activeDossier)

  useEffect(() => {
    console.log('[AUTH] redirect vers:', location.pathname)
  }, [location.pathname])

  useEffect(() => {
    console.log('[AUTH] dossier actif:', activeDossier?.id)
  }, [activeDossier?.id])

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AUTH] onAuthStateChange event:', event, session?.user?.id)
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => {
      console.log('[AUTH] user connecté:', data.user?.id, data.user?.email)
    })
  }, [location.pathname])

  return null
}

function App() {
  return (
    <BrowserRouter>
      <AuthRoutingDiagnostics />
      <Routes>
        <Route path="/" element={<MobileAwareLanding />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/f/:token" element={<FacturePublic />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/equipe/login" element={<LoginCollaborateur />} />
        <Route path="/equipe/invitation" element={<InvitationCollaborateur />} />
        <Route path="/auth/cabinet" element={<AuthCabinet />} />
        <Route path="/auth/invite" element={<AuthInvite />} />
        <Route path="/auth/verification-email" element={<AuthVerificationEmail />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><Dashboard /></Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/gestion/transactions/saisie" element={
          <ProtectedRoute>
            <Layout>
              <SaisieOperations />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/transactions/rapprochement" element={
          <Layout>
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <TransactionsRapprochement />
              </Suspense>
            </ErrorBoundary>
          </Layout>
        } />
        <Route path="/transactions/tva" element={
          <ProtectedRoute>
            <Layout>
              <TvaPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/transactions" element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Transactions />
                </Suspense>
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/bilan" element={<ProtectedRoute><Layout><CompteDeResultat /></Layout></ProtectedRoute>} />
        <Route path="/compta/balance" element={<ProtectedRoute><Layout><ComptaPlaceholder title="Balance" icon={<Scale size={40} strokeWidth={1.5} />} /></Layout></ProtectedRoute>} />
        <Route path="/compta/saisie" element={<ProtectedRoute><Layout><ComptaPlaceholder title="Saisie" icon={<PenLine size={40} strokeWidth={1.5} />} /></Layout></ProtectedRoute>} />
        <Route path="/compta/revision" element={<ProtectedRoute><Navigate to="/compta/revision/balance" replace /></ProtectedRoute>} />
        <Route path="/compta/revision/balance" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><BalanceGenerale /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/revision/grand-livre" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><GrandLivre /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/lettrage" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><LettragePage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/revision/rapprochement" element={<ProtectedRoute><Layout><RapprochementRevision /></Layout></ProtectedRoute>} />
        <Route path="/compta/revision/immobilisations" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ImmobilisationsPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/revision/immobilisations/cadrage" element={<ProtectedRoute><Navigate to="/compta/revision/immobilisations?onglet=cadrage" replace /></ProtectedRoute>} />
        <Route path="/compta/revision/feuilles" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FeuillesRevisionPage /></Suspense></Layout></ProtectedRoute>} />
        {/* Ancien builder supprimé : redirection pour favoris / historique */}
        <Route path="/compta/revision/builder" element={<ProtectedRoute><Navigate to="/compta/revision/feuilles" replace /></ProtectedRoute>} />
        <Route path="/compta/revision/builder/:modeleId" element={<ProtectedRoute><Navigate to="/compta/revision/feuilles" replace /></ProtectedRoute>} />
        <Route path="/parametres/modeles-revision" element={<ProtectedRoute><Navigate to="/parametres" replace /></ProtectedRoute>} />
        <Route path="/compta/dossier-revision" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><DossierRevisionPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/fiscalite" element={<ProtectedRoute><Layout><FiscaliteHome /></Layout></ProtectedRoute>} />
        <Route path="/compta/fiscalite/tva-board" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><DeclarationsTVABoard /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/fiscalite/tva" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><DeclarationTVA /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/fiscalite/declaration-tva" element={<ProtectedRoute><Navigate to="/compta/fiscalite/tva" replace /></ProtectedRoute>} />
        <Route path="/compta/etats-financiers" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><EtatsFinanciersPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/cloture-exercice" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ClotureExercicePage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/dossier-client" element={<ProtectedRoute><Navigate to="/compta/dossier-client/ged" replace /></ProtectedRoute>} />
        <Route path="/compta/dossier-client/ged" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><DossierClientGED /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/dossier-client/ged" element={<ProtectedRoute><Navigate to="/compta/dossier-client/ged" replace /></ProtectedRoute>} />
        <Route path="/compta/grand-livre" element={<ProtectedRoute><Navigate to="/compta/revision/grand-livre" replace /></ProtectedRoute>} />
        <Route path="/compta/journaux" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><JournauxComptables /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/journal" element={<ProtectedRoute><Navigate to="/compta/journaux" replace /></ProtectedRoute>} />
        <Route path="/compta/import-balance" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ImportBalance /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/import-fec" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ImportFEC /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/saisie/masse" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><SaisieMasse /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/saisie/kilometre" element={<ProtectedRoute><Navigate to="/compta/saisie/masse" replace /></ProtectedRoute>} />
        <Route path="/compta/regularisations/cca" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><RegularisationCCA /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/regularisations/pca" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><RegularisationPCA /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/regularisations/cap" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><RegularisationCAP /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/regularisations/par" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><RegularisationPAR /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/regularisations/fnp" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><RegularisationFNP /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/regularisations/fae" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><RegularisationFAE /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/revision/charges-constatees-avance" element={<ProtectedRoute><Navigate to={REGULARISATION_PATHS.cca} replace /></ProtectedRoute>} />
        <Route path="/compta/revision/produits-constates-avance" element={<ProtectedRoute><Navigate to={REGULARISATION_PATHS.pca} replace /></ProtectedRoute>} />
        <Route path="/compta/revision/charges-a-payer" element={<ProtectedRoute><Navigate to={REGULARISATION_PATHS.cap} replace /></ProtectedRoute>} />
        <Route path="/compta/revision/produits-a-recevoir" element={<ProtectedRoute><Navigate to={REGULARISATION_PATHS.par} replace /></ProtectedRoute>} />
        <Route path="/compta/revision/fnp" element={<ProtectedRoute><Navigate to={REGULARISATION_PATHS.fnp} replace /></ProtectedRoute>} />
        <Route path="/compta/revision/fae" element={<ProtectedRoute><Navigate to={REGULARISATION_PATHS.fae} replace /></ProtectedRoute>} />
        <Route path="/mobile/login" element={
          <Suspense fallback={<PageLoader />}>
            <MobileLogin />
          </Suspense>
        } />
        <Route path="/mobile" element={
          <MobileProtectedRoute>
            <MobileLayout />
          </MobileProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><MobileDashboard /></Suspense>} />
          <Route path="import" element={<Suspense fallback={<PageLoader />}><MobileImport /></Suspense>} />
          <Route path="factures" element={<Suspense fallback={<PageLoader />}><MobileFactures /></Suspense>} />
          <Route path="dossier" element={<Suspense fallback={<PageLoader />}><MobileDossierSelect /></Suspense>} />
          <Route path="profil" element={<Suspense fallback={<PageLoader />}><MobileProfil /></Suspense>} />
        </Route>
        <Route path="/gestion/factures-achat" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FacturesAchat /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compta/factures-achat" element={<ProtectedRoute><Layout><FacturesAchatCompta /></Layout></ProtectedRoute>} />
        <Route path="/compta/factures-vente" element={<ProtectedRoute><Layout><FacturesVenteCompta /></Layout></ProtectedRoute>} />
        <Route path="/compta/transactions" element={<ProtectedRoute><Layout><ErrorBoundary><TransactionsCompta /></ErrorBoundary></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FacturesVente /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente/import" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ImportFacturesVente /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente/importees" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FacturesVenteImportees /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente/valider/:importId" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ValiderImportVente /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente/nouvelle" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FactureVenteCreate /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente/:id/modifier" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FactureVenteCreate /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente/:id/avoir" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FactureVenteCreate /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/factures-vente/:id" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><FactureVenteDetail /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/devis" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Devis /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/devis/nouveau" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><DevisCreate /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/devis/:id/modifier" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><DevisCreate /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/devis/:id" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><DevisDetail /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/produits" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Produits /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/produits/import" element={<ProtectedRoute><Layout><ProduitsImport /></Layout></ProtectedRoute>} />
        <Route path="/gestion/clients" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Clients /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/gestion/fournisseurs" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Fournisseurs /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/compte/*" element={<ProtectedRoute><Layout><MonCompte /></Layout></ProtectedRoute>} />
        <Route path="/parametres" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Parametres /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/parametres/entreprise" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ParametresEntreprise /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/parametres/exercice" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ParametresExercice /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/parametres/plan-comptable" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><PlanComptable /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/cabinet" element={
          <ProtectedRoute>
            <Layout><Suspense fallback={<PageLoader />}><CabinetDashboard /></Suspense></Layout>
          </ProtectedRoute>
        } />
        <Route path="/cabinet/organisation" element={
          <ProtectedRoute>
            <Layout><Suspense fallback={<PageLoader />}><OrganisationCabinet /></Suspense></Layout>
          </ProtectedRoute>
        } />
        <Route path="/export/operations" element={
          <ProtectedRoute>
            <Layout><ExportOperations /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/cockpit" element={<CockpitLayout />}>
          <Route index element={<CockpitDashboard />} />
          <Route path="users" element={<CockpitUsers />} />
          <Route path="users/:id" element={<CockpitUserDetail />} />
          <Route path="dossiers" element={<CockpitDossiers />} />
          <Route path="dossiers/:id" element={<CockpitDossierDetail />} />
          <Route path="abonnements" element={<CockpitAbonnements />} />
          <Route path="activite" element={<CockpitActivite />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export default App